<template>
  <div
    v-if="loading || (status && status !== 'too_old')"
    class="shipment-tracking py-2 px-4"
  >
    <!-- Loading state -->
    <div v-if="loading" class="d-flex align-center">
      <VProgressCircular indeterminate size="16" width="2" class="mr-2" />
      <span class="caption text--secondary">
        Fetching tracking info&hellip;
      </span>
    </div>

    <!-- No info available -->
    <div v-else-if="status === 'no_info'" class="caption text--secondary">
      <VIcon x-small class="mr-1">$info-circle</VIcon>
      No tracking info available.
      <a
        v-if="fallbackTrackingUrl"
        :href="fallbackTrackingUrl"
        target="_blank"
        rel="noopener"
      >
        Check {{ carrierName }} website
      </a>
    </div>

    <!-- Error -->
    <div v-else-if="status === 'error'" class="caption error--text">
      <VIcon x-small color="error" class="mr-1">$exclamation-triangle</VIcon>
      {{ errorMessage }}
    </div>

    <!-- Tracking data available -->
    <div v-else-if="status === 'available' && trackingData">
      <!-- Status + location + date on one line -->
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <VIcon x-small :color="statusColor" class="mr-1">
            {{ statusIcon }}
          </VIcon>
          <span
            class="caption font-weight-bold"
            :class="statusColor + '--text'"
          >
            {{ statusLabel }}
          </span>
          <span v-if="statusLocation" class="caption text--secondary ml-1">
            &mdash; {{ statusLocation }}
          </span>
        </div>
        <span
          v-if="trackingData.tracking_status"
          class="caption text--secondary"
          :title="verboseDate(trackingData.tracking_status.status_date)"
        >
          {{
            formatDateDistanceToNowIfRecent(
              trackingData.tracking_status.status_date
            )
          }}
        </span>
      </div>

      <!-- Route + service level -->
      <div class="caption text--secondary">
        <template v-if="trackingData.address_from && trackingData.address_to">
          {{ formatLocation(trackingData.address_from) }}
          &rarr;
          {{ formatLocation(trackingData.address_to) }}
        </template>
        <template
          v-if="trackingData.servicelevel && trackingData.servicelevel.name"
        >
          <template v-if="trackingData.address_from && trackingData.address_to">
            &bull;
          </template>
          {{ trackingData.servicelevel.name }}
        </template>
        <template v-if="trackingData.eta">
          &bull; ETA:
          <span :title="verboseDate(trackingData.eta)">
            {{ formatDateDistanceToNowIfRecent(trackingData.eta) }}
          </span>
        </template>
      </div>

      <!-- Tracking history timeline -->
      <div v-if="trackingData.tracking_history.length">
        <a
          class="caption text-decoration-none"
          @click="showHistory = !showHistory"
        >
          {{ showHistory ? 'Hide' : 'Show' }} history ({{
            trackingData.tracking_history.length
          }}
          events)
        </a>
        <VExpandTransition>
          <div v-show="showHistory" class="mt-1">
            <div
              v-for="(event, i) in reversedHistory"
              :key="i"
              class="tracking-event d-flex"
            >
              <div class="tracking-timeline mr-2">
                <div
                  class="tracking-dot"
                  :class="i === 0 ? statusColor : 'grey lighten-1'"
                />
                <div
                  v-if="i < reversedHistory.length - 1"
                  class="tracking-line"
                />
              </div>
              <div class="tracking-event-content pb-2">
                <div class="caption font-weight-medium">
                  {{ event.status_details }}
                </div>
                <div
                  class="caption text--secondary"
                  :title="verboseDate(event.status_date)"
                >
                  {{ formatDateDistanceToNowIfRecent(event.status_date) }}
                  <template v-if="formatEventLocation(event.location)">
                    &mdash; {{ formatEventLocation(event.location) }}
                  </template>
                </div>
              </div>
            </div>
          </div>
        </VExpandTransition>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref, watch } from 'vue'
import { getModule } from 'vuex-module-decorators'

import { formatDateDistanceToNowIfRecent } from '@/components/mixins/dateFormatters'
import {
  TrackingLocation,
  TrackingResponse,
  TrackingResult
} from '@/services/shipmentTracking'
import {
  fetchShipmentTracking,
  getTrackingEntry
} from '@/services/trackingCache'
import Salesforce from '@/store/modules/salesforce'
import { Shipment } from '@/store/modules/types/order'
import {
  formatDateVerboseWithDestination,
  timezoneForState
} from '@/utils/timezone'

export default defineComponent({
  props: {
    shipment: {
      type: Object as PropType<Shipment>,
      required: true
    }
  },
  setup(props) {
    const salesforce = getModule(Salesforce)
    const loading = ref(false)
    const result = ref<TrackingResult | null>(null)
    const showHistory = ref(false)

    async function doFetch() {
      if (!props.shipment.trackingNumber) return

      // Check cache first (covers demo mode where cache is pre-seeded)
      const cached = getTrackingEntry(props.shipment.trackingNumber)
      if (cached?.result) {
        result.value = cached.result
        return
      }

      if (!salesforce.accessToken) return

      loading.value = true
      result.value = await fetchShipmentTracking(
        props.shipment,
        salesforce.accessToken
      )
      loading.value = false
    }

    onMounted(doFetch)
    watch(() => props.shipment.trackingNumber, doFetch)

    const status = computed(() => result.value?.status ?? null)

    const trackingData = computed<TrackingResponse | null>(() => {
      const r = result.value
      return r?.status === 'available' ? r.data : null
    })

    const carrierName = computed(() => {
      const r = result.value
      if (r && (r.status === 'too_old' || r.status === 'no_info')) {
        return r.carrier.toUpperCase()
      }
      return ''
    })

    const fallbackTrackingUrl = computed(() => {
      const r = result.value
      if (r && (r.status === 'too_old' || r.status === 'no_info')) {
        return r.trackingUrl
      }
      return ''
    })

    const errorMessage = computed(() => {
      const r = result.value
      return r?.status === 'error' ? r.message : ''
    })

    const currentStatus = computed(() => {
      return trackingData.value?.tracking_status?.status ?? ''
    })

    const statusColor = computed(() => {
      switch (currentStatus.value) {
        case 'DELIVERED':
          return 'green'
        case 'TRANSIT':
          return 'blue'
        case 'PRE_TRANSIT':
          return 'grey'
        case 'FAILURE':
        case 'RETURNED':
          return 'red'
        default:
          return 'grey'
      }
    })

    const statusIcon = computed(() => {
      switch (currentStatus.value) {
        case 'DELIVERED':
          return '$check-square'
        case 'TRANSIT':
          return '$truck'
        case 'PRE_TRANSIT':
          return '$info-circle'
        case 'FAILURE':
        case 'RETURNED':
          return '$exclamation-triangle'
        default:
          return '$info-circle'
      }
    })

    const statusLabel = computed(() => {
      switch (currentStatus.value) {
        case 'DELIVERED':
          return 'Delivered'
        case 'TRANSIT':
          return 'In Transit'
        case 'PRE_TRANSIT':
          return 'Pre-Transit'
        case 'FAILURE':
          return 'Delivery Failed'
        case 'RETURNED':
          return 'Returned to Sender'
        default:
          return currentStatus.value || 'Unknown'
      }
    })

    const statusLocation = computed(() => {
      const loc = trackingData.value?.tracking_status?.location
      return loc ? formatEventLocation(loc) : ''
    })

    const destinationTimezone = computed(() => {
      const state = trackingData.value?.address_to?.state
      return timezoneForState(state) || null
    })

    function verboseDate(dateStr: string): string {
      return formatDateVerboseWithDestination(
        dateStr,
        destinationTimezone.value
      )
    }

    const reversedHistory = computed(() => {
      return [...(trackingData.value?.tracking_history || [])].reverse()
    })

    function formatLocation(loc: TrackingLocation): string {
      const parts = [loc.city, loc.state].filter(Boolean)
      return parts.join(', ')
    }

    function formatEventLocation(loc: TrackingLocation | null): string {
      if (!loc) return ''
      const parts = [loc.city, loc.state].filter(Boolean)
      return parts.join(', ')
    }

    return {
      loading,
      status,
      trackingData,
      carrierName,
      fallbackTrackingUrl,
      errorMessage,
      statusColor,
      statusIcon,
      statusLabel,
      statusLocation,
      reversedHistory,
      showHistory,
      formatDateDistanceToNowIfRecent,
      verboseDate,
      formatLocation,
      formatEventLocation
    }
  }
})
</script>

<style scoped>
.shipment-tracking {
  background: #fafafa;
  border-radius: 4px;
}
.tracking-timeline {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 12px;
}
.tracking-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 4px;
  flex-shrink: 0;
}
.tracking-dot.green {
  background: #4caf50;
}
.tracking-dot.blue {
  background: #2196f3;
}
.tracking-dot.grey {
  background: #bdbdbd;
}
.tracking-dot.red {
  background: #f44336;
}
.tracking-line {
  width: 1px;
  flex-grow: 1;
  background: #e0e0e0;
  margin: 2px 0;
}
</style>
